import { Paper } from '@mui/material'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import { fireReactGAEvent, questionFontStyle } from '../../Questions/utils'
import { ApplicationFormContext } from '../ApplicationFormContext'
import QuestionKey from '../../../Questions/question-key'
import AppFormQuestion from '../../AppFormQuestion'
import withFade from '../../HOC/withFade'
import withQuestion from '../../HOC/withQuestion'
import QuestionComponentProps from '../../../Questions/question-component-props'
import { 
    checkErrs, 
    createValues, 
    createBoolValues, 
    createValidationErrors, 
    createDebounceMap, 
    FieldValues, 
    FieldBools,
    FieldFnVoid, 
    FieldKey 
} from '../../../utils/RequiredDetailsPageApplicationForm-utils'


const RequiredDetailsFields = ({
    requiredFields = {}
}: QuestionComponentProps): JSX.Element => {
    const { dispatch } = useContext(ApplicationFormContext)
    const [, setFocus] = useState<FieldKey | null>(null)

    const [values, setValues] = useState<FieldValues>(createValues(requiredFields))
    const [showErrors, setShowErrors] = useState<FieldBools>(createBoolValues(requiredFields))
    const [dirtyFields, setDirtyFields] = useState<FieldBools>(createBoolValues(requiredFields))

    const validationErrors: FieldValues = createValidationErrors(requiredFields, values)

    const errors = Object.values(validationErrors)

    const debouncedSetShowError = (key: FieldKey) => {
        setShowErrors((previous) => ({ ...previous, [key]: true }))
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Validation Error',
          label: key as string,
          nonInteraction: true
        })
    }

    const debounceMap: FieldFnVoid = useMemo(() => {
        return createDebounceMap(requiredFields, debouncedSetShowError)
    }, [requiredFields])

    const setShouldShowError = useCallback((
        key: FieldKey, 
        errorMessage: string, 
        isDirty: boolean,
        showError?: boolean
    ): void => {
        if (!(isDirty && errorMessage)) {
            setShowErrors((previous) => ({ ...previous, [key]: false }))
            return
        } 
        if (showError) {
            setShowErrors((previous) => ({ ...previous, [key]: true }))
            return
        }
        debounceMap[key]()
    }, [])

    const onChange = useCallback((key: FieldKey, value: string) => {
        if (!dirtyFields[key]) setDirtyFields((previous) => ({ ...previous, [key]: true }))
        setValues((previous) => ({ ...previous, [key]: value }))
    }, [])

    useEffect(() => {
        const noErrs = checkErrs(errors)
        dispatch({
            type: 'SAVE',
            name: QuestionKey.REQUIRED_DETAILS, 
            value: noErrs ? values : {},
            errors: showErrors
        })
    }, [values.contactNumber, values.jobTitle])

    // Dynamically create useEffect hooks for each field
    useEffect(() => {
        Object.keys(requiredFields).forEach((key) => {
            const fieldKey = key as FieldKey
            const error = validationErrors[fieldKey]
            setShouldShowError(fieldKey, error, !!dirtyFields[fieldKey], showErrors[fieldKey])
        })
    }, [values])

    return (
        <Paper elevation={6} sx={{ p: 5, borderRadius: '12px' }}>
            <AppFormQuestion questionKey={QuestionKey.REQUIRED_DETAILS} mt={3} mb={3}  textStyle={questionFontStyle}>
                Required Details
            </AppFormQuestion>
            <MuiTextField
                onFocus={() => setFocus('contactNumber')}
                aria-label="Mobile number"
                autoComplete="tel"
                data-hj-suppress=""
                errorMessage={showErrors.contactNumber ? validationErrors.contactNumber : undefined}
                id="contactNumber"
                inputMaxLength={20}
                inputType="tel"
                inputVariant="outlined"
                label="Mobile number"
                mb="34px"
                {...(requiredFields?.jobTitle && { multipleInputs: true })}
                name="contactNumber"
                onChangeHandler={(event) => onChange('contactNumber', event.currentTarget.value)}
                testId="contactNumber"
            />
           {requiredFields?.jobTitle && <MuiTextField
                onFocus={() => setFocus('jobTitle')}
                aria-label="Job title"
                data-hj-suppress=""
                errorMessage={showErrors.jobTitle ? validationErrors.jobTitle : undefined}
                id="jobTitle"
                inputMaxLength={100}
                inputType="text"
                inputVariant="outlined"
                label="Job title"
                mb="34px"
                name="jobTitle"
                onChangeHandler={(event) => onChange('jobTitle', event.currentTarget.value)}
                testId="jobTitle"
            />}
        </Paper>
    )
}

export default withFade(withQuestion(RequiredDetailsFields))
